import CheckboxGroup from "../../applicationUi/components/intake/CheckboxGroup";
import RadioGroup from "../../applicationUi/components/intake/RadioGroup";
import InputField from "../../applicationUi/components/intake/InputField";
import TextAreaField from "../../applicationUi/components/intake/TextAreaField"; // Assuming you have this component for text areas

const PreschoolLanguageSection = ({ formData, handleChange, isReadOnly = false }) => (
  <section className="space-y-8">
    <div className="space-y-2">
      <h2 className="text-xl font-semibold text-foreground">A - Preschool Language</h2>
      <p className="text-sm text-muted-foreground">
        This section applies to children with less than 50 words or under 3 years of age.
      </p>
    </div>

    {/* Less than 50 Words RadioGroup */}
    <RadioGroup
      label="Does your child have less than 50 words?"
      name="preschoolLanguage.lessThan50Words"
      options={[
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ]}
      selectedValue={formData.preschoolLanguage.lessThan50Words}
      onChange={handleChange}
      readOnly={isReadOnly}
    />

    {/* If No, move to Section B */}
    {formData.preschoolLanguage.lessThan50Words === "no" && (
      <p className="text-sm text-gray-500">Please proceed to Section B.</p>
    )}

    {/* Show only if less than 50 words */}
    {formData.preschoolLanguage.lessThan50Words === "yes" && (
      <>
        {/* Multiple checkbox options for knowledge */}
        <div className="space-y-4">
          <label className="text-sm font-medium text-foreground block">Does your child know the following?</label>
          <div className="space-y-3">
            {[
              { name: "knowsName", label: "Their Name" },
              { name: "knowsNumbers1to10", label: "Numbers 1-10" },
              { name: "knowsBodyParts", label: "Body Parts" },
              { name: "knowsBasicColors", label: "Basic Colors" },
            ].map((option) => (
              <div
                key={option.name}
                className={`flex items-start space-x-3 p-3 rounded-sm border transition-colors ${
                  isReadOnly
                    ? "border-gray-200 bg-gray-50"
                    : formData.preschoolLanguage?.[option.name]
                    ? "border-primary bg-primary/5"
                    : "border-gray-200 bg-background hover:border-gray-300 hover:bg-accent/30"
                }`}
              >
                <input
                  type="checkbox"
                  id={`preschoolLanguage-${option.name}`}
                  name={`preschoolLanguage.${option.name}`}
                  checked={formData.preschoolLanguage?.[option.name] || false}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className={`h-4 w-4 rounded-sm border transition-colors focus:ring-1 focus:ring-ring focus:ring-offset-0 ${
                    isReadOnly
                      ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                      : "border-gray-200 text-primary hover:border-gray-300 focus:border-primary"
                  }`}
                />
                <label
                  htmlFor={`preschoolLanguage-${option.name}`}
                  className={`text-sm leading-relaxed ${
                    isReadOnly ? "text-muted-foreground cursor-not-allowed" : "text-foreground cursor-pointer"
                  }`}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Multiple checkbox options for understanding */}
        <div className="space-y-4">
          <label className="text-sm font-medium text-foreground block">Does your child understand the following?</label>
          <div className="space-y-3">
            {[
              {
                name: "understandsShortRequests",
                label: "Short requests (e.g., 'Get your shoes')",
              },
              {
                name: "understandsStories",
                label: "Stories like 'The Three Bears'",
              },
              {
                name: "understandsChildrensTvPrograms",
                label: "Children's TV programs",
              },
              {
                name: "understandsAdultConversations",
                label: "Adult conversations",
              },
            ].map((option) => (
              <div
                key={option.name}
                className={`flex items-start space-x-3 p-3 rounded-sm border transition-colors ${
                  isReadOnly
                    ? "border-gray-200 bg-gray-50"
                    : formData.preschoolLanguage?.[option.name]
                    ? "border-primary bg-primary/5"
                    : "border-gray-200 bg-background hover:border-gray-300 hover:bg-accent/30"
                }`}
              >
                <input
                  type="checkbox"
                  id={`preschoolLanguage-${option.name}`}
                  name={`preschoolLanguage.${option.name}`}
                  checked={formData.preschoolLanguage?.[option.name] || false}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className={`h-4 w-4 rounded-sm border transition-colors focus:ring-1 focus:ring-ring focus:ring-offset-0 ${
                    isReadOnly
                      ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                      : "border-gray-200 text-primary hover:border-gray-300 focus:border-primary"
                  }`}
                />
                <label
                  htmlFor={`preschoolLanguage-${option.name}`}
                  className={`text-sm leading-relaxed ${
                    isReadOnly ? "text-muted-foreground cursor-not-allowed" : "text-foreground cursor-pointer"
                  }`}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Multiple checkbox options for communication methods */}
        <div className="space-y-4">
          <label className="text-sm font-medium text-foreground block">Does your child communicate in the following ways? (Check all that apply)</label>
          <div className="space-y-3">
            {[
              {
                name: "eyeContactBodyMovements",
                label: "Eye contact / Body movements",
              },
              { name: "sounds", label: "Sounds" },
              { name: "gestures", label: "Gestures" },
            ].map((option) => (
              <div
                key={option.name}
                className={`flex items-start space-x-3 p-3 rounded-sm border transition-colors ${
                  isReadOnly
                    ? "border-gray-200 bg-gray-50"
                    : formData.preschoolLanguage.communicates?.[option.name]
                    ? "border-primary bg-primary/5"
                    : "border-gray-200 bg-background hover:border-gray-300 hover:bg-accent/30"
                }`}
              >
                <input
                  type="checkbox"
                  id={`preschoolLanguageCommunicates-${option.name}`}
                  name={`preschoolLanguage.communicates.${option.name}`}
                  checked={formData.preschoolLanguage.communicates?.[option.name] || false}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className={`h-4 w-4 rounded-sm border transition-colors focus:ring-1 focus:ring-ring focus:ring-offset-0 ${
                    isReadOnly
                      ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                      : "border-gray-200 text-primary hover:border-gray-300 focus:border-primary"
                  }`}
                />
                <label
                  htmlFor={`preschoolLanguageCommunicates-${option.name}`}
                  className={`text-sm leading-relaxed ${
                    isReadOnly ? "text-muted-foreground cursor-not-allowed" : "text-foreground cursor-pointer"
                  }`}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Speech Understanding - Drop-down */}
        <InputField
          label="Can you understand your child's speech?"
          name="preschoolLanguage.understandingSpeech"
          value={formData.preschoolLanguage.understandingSpeech}
          onChange={handleChange}
      readOnly={isReadOnly}
        />

        {/* Communication with Other Children */}
        <div className="space-y-4">
          <label className="text-sm font-medium text-foreground block">How does your child communicate and interact with other children? (Check all that apply)</label>
          <div className="space-y-3">
            {[
              {
                name: "eyeContactBodyMovements",
                label: "Eye contact / Body movements",
              },
              { name: "playsAlone", label: "Not at all; plays alone" },
            ].map((option) => (
              <div
                key={option.name}
                className={`flex items-start space-x-3 p-3 rounded-sm border transition-colors ${
                  isReadOnly
                    ? "border-gray-200 bg-gray-50"
                    : formData.preschoolLanguage.communicatesWithOtherChildren?.[option.name]
                    ? "border-primary bg-primary/5"
                    : "border-gray-200 bg-background hover:border-gray-300 hover:bg-accent/30"
                }`}
              >
                <input
                  type="checkbox"
                  id={`communicatesWithOtherChildren-${option.name}`}
                  name={`preschoolLanguage.communicatesWithOtherChildren.${option.name}`}
                  checked={formData.preschoolLanguage.communicatesWithOtherChildren?.[option.name] || false}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className={`h-4 w-4 rounded-sm border transition-colors focus:ring-1 focus:ring-ring focus:ring-offset-0 ${
                    isReadOnly
                      ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                      : "border-gray-200 text-primary hover:border-gray-300 focus:border-primary"
                  }`}
                />
                <label
                  htmlFor={`communicatesWithOtherChildren-${option.name}`}
                  className={`text-sm leading-relaxed ${
                    isReadOnly ? "text-muted-foreground cursor-not-allowed" : "text-foreground cursor-pointer"
                  }`}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        <TextAreaField
          label="Other (please specify)"
          name="preschoolLanguage.communicatesWithOtherChildren.other"
          value={formData.preschoolLanguage.communicatesWithOtherChildren.other}
          onChange={handleChange}
      readOnly={isReadOnly}
        />

        {/* What does your child like to communicate most? */}
        <div className="space-y-4">
          <label className="text-sm font-medium text-foreground block">What does your child like to communicate most to you? (Check all that apply)</label>
          <div className="space-y-3">
            {[
              {
                name: "whatTheyAreDoing",
                label: "What they are doing at the time",
              },
              { name: "whatYouAreDoing", label: "What you are doing" },
            ].map((option) => (
              <div
                key={option.name}
                className={`flex items-start space-x-3 p-3 rounded-sm border transition-colors ${
                  isReadOnly
                    ? "border-gray-200 bg-gray-50"
                    : formData.preschoolLanguage.whatChildLikesToCommunicate?.[option.name]
                    ? "border-primary bg-primary/5"
                    : "border-gray-200 bg-background hover:border-gray-300 hover:bg-accent/30"
                }`}
              >
                <input
                  type="checkbox"
                  id={`whatChildLikesToCommunicate-${option.name}`}
                  name={`preschoolLanguage.whatChildLikesToCommunicate.${option.name}`}
                  checked={formData.preschoolLanguage.whatChildLikesToCommunicate?.[option.name] || false}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className={`h-4 w-4 rounded-sm border transition-colors focus:ring-1 focus:ring-ring focus:ring-offset-0 ${
                    isReadOnly
                      ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                      : "border-gray-200 text-primary hover:border-gray-300 focus:border-primary"
                  }`}
                />
                <label
                  htmlFor={`whatChildLikesToCommunicate-${option.name}`}
                  className={`text-sm leading-relaxed ${
                    isReadOnly ? "text-muted-foreground cursor-not-allowed" : "text-foreground cursor-pointer"
                  }`}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        <TextAreaField
          label="Other (please specify)"
          name="preschoolLanguage.whatChildLikesToCommunicate.other"
          value={formData.preschoolLanguage.whatChildLikesToCommunicate.other}
          onChange={handleChange}
      readOnly={isReadOnly}
        />
      </>
    )}
  </section>
);

export default PreschoolLanguageSection;
